import { Button } from '@monorepo/shared/componentsV2/Button';
import { TextField } from '@monorepo/shared/componentsV2/fields/TextField';
import { RadioGroupField } from '@monorepo/shared/componentsV2/fields/RadioGroupField';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { RadioGroupOption } from '@monorepo/shared/componentsV2/RadioGroup';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import {
  compose,
  isRequired as isRequiredValidator,
} from '@monorepo/shared/utils/validators';
import {
  DocumentFolderScope,
  DocumentFolderWithFileCountResponse,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useCreateDocumentFolder } from '../../hooks/useCreateDocumentFolder';
import { useRenameDocumentFolder } from '../../hooks/useRenameDocumentFolder';

interface FolderFormModalProps {
  organizationId: string;
  documentFolders: DocumentFolderWithFileCountResponse[];
  onClose: () => void;
  onFolderCreated?: (folderId: string) => void;
  folder?: DocumentFolderWithFileCountResponse;
}

interface FormValues {
  name: string;
  scope: DocumentFolderScope;
}

const isUniqueFolderName =
  (
    documentFolders: DocumentFolderWithFileCountResponse[],
    {
      isEditing,
      existingFolderId,
    }: { isEditing: boolean; existingFolderId?: string },
  ) =>
  (value?: unknown) => {
    const trimmed = (value ?? '').toString().trim();
    if (!trimmed) return undefined;
    const conflictsWithExisting = documentFolders.some((existing) => {
      if (isEditing && existingFolderId === existing.id) return false;
      return existing.name.toLowerCase() === trimmed.toLowerCase();
    });
    return conflictsWithExisting
      ? 'A folder with this name already exists. Folder names must be unique.'
      : undefined;
  };

export function FolderFormModal({
  onClose,
  organizationId,
  documentFolders,
  folder: existingFolder,
  onFolderCreated,
}: FolderFormModalProps) {
  const isEditing = existingFolder !== undefined;
  const { showUserFriendlyErrorToast, success } = useToast();

  const [createFolder] = useCreateDocumentFolder({
    config: { throwOnError: true },
  });
  const [renameFolder] = useRenameDocumentFolder({
    config: { throwOnError: true },
  });

  const scopeOptions: RadioGroupOption[] = useMemo(
    () => [
      {
        supertitle: 'Organization managed documents',
        label:
          'One set of files shared organization-wide (e.g. SDS, SOPs, policies). Only organization admins can edit these documents.',
        value: DocumentFolderScope.ORGANIZATION,
      },
      {
        supertitle: 'Site managed documents',
        label:
          'Each site manages its own files in this folder. Organization and site admins can edit these documents.',
        value: DocumentFolderScope.PROJECT,
      },
    ],
    [],
  );

  const nameValidator = useMemo(
    () =>
      compose(
        isRequiredValidator,
        isUniqueFolderName(documentFolders, {
          isEditing,
          existingFolderId: existingFolder?.id,
        }),
      ),
    [documentFolders, existingFolder?.id, isEditing],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const name = values.name.trim();
      try {
        if (isEditing) {
          await renameFolder({
            organizationId,
            folderId: existingFolder.id,
            folder: { name },
          });
          success(`Folder was renamed to "${name}".`);
        } else {
          const createdFolder = await createFolder({
            organizationId,
            folder: { name, scope: values.scope },
          });
          if (createdFolder) {
            onFolderCreated?.(createdFolder.id);
          }
          success(`Folder "${name}" was created.`);
        }
        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(
          err,
          isEditing ? 'Unable to rename folder.' : 'Unable to create folder.',
        );
      }
    },
    [
      createFolder,
      existingFolder,
      isEditing,
      onClose,
      onFolderCreated,
      organizationId,
      renameFolder,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  const initialValues: FormValues = isEditing
    ? { name: existingFolder.name, scope: existingFolder.scope }
    : { name: '', scope: DocumentFolderScope.PROJECT };

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{
        submitting: true,
        pristine: true,
        hasValidationErrors: true,
      }}
    >
      {({ handleSubmit, submitting, pristine, hasValidationErrors }) => {
        let saveButtonLabel = isEditing ? 'Save' : 'Create folder';
        if (submitting) {
          saveButtonLabel = isEditing ? 'Saving...' : 'Creating...';
        }

        return (
          <BaseModal
            open
            onClose={submitting ? undefined : onClose}
            showCloseButton
            title={isEditing ? 'Rename folder' : 'Create folder'}
            maxWidth="sm"
            buttons={[
              <Button
                key="cancel"
                variant="text"
                color="primary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>,
              <Button
                key="save"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting || pristine || hasValidationErrors}
              >
                {saveButtonLabel}
              </Button>,
            ]}
          >
            <TextField
              name="name"
              label="Folder name"
              autoFocus
              required
              validate={nameValidator}
              forceShowErrors={false}
            />
            {!isEditing && (
              <RadioGroupField
                name="scope"
                label="How do you want to manage the documents in this folder?"
                options={scopeOptions}
                required
                validate={isRequiredValidator}
                variant="outlined"
                forceShowErrors={false}
              />
            )}
          </BaseModal>
        );
      }}
    </Form>
  );
}
